<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b">
        <div class="flex items-center gap-3">
          <img :src="publicPath + 'images/integrations/google-sheets.png'" class="w-6 h-6" alt="Google Sheets" />
          <h3 class="text-lg font-semibold text-gray-800">{{ t('google_sheets_sync') }}</h3>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="px-6 py-4 space-y-5">
        <!-- Not Connected Warning -->
        <div v-if="!isConnected" class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p class="text-yellow-800 text-sm" v-html="t('sheets_connect_first_warning')"></p>
        </div>

        <template v-else>
          <!-- Skeleton Loading (only when sheet is configured and loading) -->
          <div v-if="isLoadingSheets && syncSettings.spreadsheet_id" class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div class="animate-pulse space-y-4">
              <div class="h-4 w-20 bg-gray-200 rounded"></div>
              <div class="h-10 bg-gray-200 rounded"></div>
              <div class="flex gap-4">
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-24 bg-gray-200 rounded"></div>
                  <div class="h-10 bg-gray-200 rounded"></div>
                </div>
                <div class="flex-1 space-y-2">
                  <div class="h-4 w-24 bg-gray-200 rounded"></div>
                  <div class="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Settings Card (show immediately when not configured, or after loading when configured) -->
          <div v-else class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 class="text-sm font-semibold text-gray-800 mb-4">{{ t('settings') }}</h4>

            <!-- Spreadsheet Selection (hidden in simple mode) -->
            <div v-if="!simple" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('spreadsheet') }}</label>

              <!-- Current Selection -->
              <div v-if="syncSettings.spreadsheet_id" class="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded">
                <img :src="publicPath + 'images/integrations/google-sheets.png'" class="w-5 h-5" alt="Sheet" />
                <span class="flex-1 text-sm text-green-800 truncate">{{ syncSettings.spreadsheet_name || syncSettings.spreadsheet_id }}</span>
                <a :href="'https://docs.google.com/spreadsheets/d/' + syncSettings.spreadsheet_id" target="_blank" class="text-indigo-600 hover:text-indigo-700 text-sm">{{ t('open') }}</a>
                <button @click="clearSpreadsheet" type="button" class="text-gray-400 hover:text-red-500 text-sm">{{ t('change') }}</button>
              </div>

              <!-- Selection UI -->
              <div v-else>
                <!-- Select Mode -->
                <template v-if="!showCreateMode">
                  <div class="flex gap-2 items-center">
                    <select
                      v-model="selectedSheetId"
                      class="flex-1 border border-gray-300 rounded px-3 h-10 focus:ring-2 focus:ring-indigo-500 text-sm"
                      :disabled="isLoadingSheets"
                    >
                      <option value="">{{ isLoadingSheets ? t('loading') : t('select_a_spreadsheet') }}</option>
                      <option v-for="sheet in spreadsheets" :key="sheet.id" :value="sheet.id">
                        {{ sheet.name }}
                      </option>
                    </select>
                    <Button @click="confirmSelectSheet" :disabled="!selectedSheetId || isSelectingSheet">
                      <svg v-if="isSelectingSheet" class="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      {{ isSelectingSheet ? t('setting_up') : t('select') }}
                    </Button>
                  </div>
                  <p class="mt-2 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                    {{ t('select_sheet_warning') }}
                  </p>
                  <a @click="showCreateMode = true" class="mt-2 inline-block text-sm text-indigo-600 hover:text-indigo-700 cursor-pointer">
                    {{ t('create_new_spreadsheet') }}
                  </a>
                </template>

                <!-- Create Mode -->
                <template v-else>
                  <div class="flex gap-2 items-center">
                    <input
                      v-model="newSheetName"
                      type="text"
                      :placeholder="t('enter_spreadsheet_name')"
                      class="flex-1 border border-gray-300 rounded px-3 h-10 focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                    <Button @click="createNewSheet" :disabled="!newSheetName || isCreatingSheet">
                      <svg v-if="isCreatingSheet" class="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      {{ isCreatingSheet ? t('creating') : t('create') }}
                    </Button>
                  </div>
                  <a @click="showCreateMode = false" class="mt-2 inline-block text-sm text-indigo-600 hover:text-indigo-700 cursor-pointer">
                    {{ t('or_select_from_existing_spreadsheets') }}
                  </a>
                </template>
              </div>
            </div>

            <!-- Sync Frequency & Interval -->
            <div class="flex gap-4 mb-4 transition-opacity duration-200" :class="{ 'opacity-50 pointer-events-none': isSaving || isSyncing || isAutoSaving }">
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('sync_frequency') }}</label>
                <Select
                  v-model="syncSettings.sync_mode"
                  :options="syncModeOptions"
                  :placeholder="t('select_sync_mode')"
                  :search="false"
                  white
                  :readonly="isSaving || isSyncing || isAutoSaving"
                />
              </div>
              <div v-if="syncSettings.sync_mode === 'scheduled'" class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('sync_interval') }}</label>
                <Select
                  v-model="syncSettings.sync_interval"
                  :options="intervalOptions"
                  :placeholder="t('select_interval')"
                  :search="false"
                  white
                  :readonly="isSaving || isSyncing || isAutoSaving"
                />
              </div>
            </div>

            <!-- More Settings Link -->
            <div v-if="simple" class="flex items-center justify-end">
              <a href="admin.php?page=formychat-integrations" class="text-sm text-indigo-600 hover:text-indigo-700">
                {{ t('more_settings_arrow') }}
              </a>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <svg class="w-4 h-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <p class="text-red-800 text-sm">{{ error }}</p>
          </div>

          <!-- Sync Status -->
          <div class="flex items-center justify-between pt-4 border-t border-gray-200">
            <div class="flex items-center gap-4 text-sm">
              <span><span class="font-semibold text-green-600">{{ syncStats.synced }}</span> {{ t('synced') }}</span>
              <span><span class="font-semibold text-yellow-600">{{ syncStats.pending }}</span> {{ t('pending') }}</span>
              <span class="text-gray-400">{{ t('last') }}: {{ formattedLastSync }}</span>
            </div>
            <button
              @click="triggerManualSync"
              :disabled="isSyncing || !syncSettings.spreadsheet_id"
              class="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{
                'bg-green-600 hover:bg-green-700 text-white': !syncButtonState,
                'bg-green-600 text-white': isSyncing,
                'bg-green-100 text-green-700 border border-green-300': syncButtonState === 'synced'
              }"
            >
              <svg v-if="syncButtonState === 'synced'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <svg v-else class="w-4 h-4" :class="{ 'animate-spin': isSyncing }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ isSyncing ? t('syncing') : (syncButtonState === 'synced' ? t('synced_exclamation') : t('sync_now')) }}
            </button>
          </div>

          <!-- Free Version Limit Warning -->
          <div v-if="!isPremium && syncStats.synced >= freeLimit" class="mt-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
            {{ t('free_version_limit_reached', { limit: freeLimit }) }}
          </div>
        </template>
      </div>

      <!-- Resync Confirmation Dialog -->
      <div v-if="showResyncConfirmation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 class="text-lg font-semibold text-gray-800 mb-3">{{ t('full_resync') }}</h3>
          <p class="text-gray-600 mb-4 text-sm">{{ t('full_resync_warning') }}</p>
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p class="text-yellow-800 text-sm">{{ t('backup_spreadsheet_warning') }}</p>
          </div>
          <div class="flex justify-end gap-3">
            <button
              @click="showResyncConfirmation = false"
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition duration-150"
            >
              {{ t('cancel') }}
            </button>
            <button
              @click="executeFullResync"
              class="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition duration-150"
            >
              {{ t('yes_resync') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@util/API'
import { Select, Button } from '@components'
import { useTranslation } from '../../composables/useTranslation'

export default {
  name: 'SyncSettingsModal',
  components: { Select, Button },
  setup() {
    const { t } = useTranslation()
    return { t }
  },
  emits: ['close', 'saved', 'stats-updated'],
  props: {
    simple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isConnected: false,
      spreadsheets: [],
      syncSettings: {
        spreadsheet_id: '',
        spreadsheet_name: '',
        sync_mode: 'scheduled',
        sync_interval: '1h',
        last_sync_at: null,
      },
      syncStats: {
        synced: 0,
        pending: 0,
        total: 0,
      },
      freeLimit: 100,
      intervals: [],
      isLoadingSheets: false,
      isSaving: false,
      isSyncing: false,
      error: '',
      success: '',
      // Spreadsheet selection
      showCreateMode: false,
      selectedSheetId: '',
      isSelectingSheet: false,
      newSheetName: '',
      isCreatingSheet: false,
      showResyncConfirmation: false,
      syncButtonState: null,
      isAutoSaving: false,
      isDataLoaded: false,
    }
  },
  computed: {
    isPremium() {
      return window.formychat_admin_vars?.is_premium || false
    },
    publicPath() {
      return window.formychat_admin_vars?.public || ''
    },
    syncModeOptions() {
      return [
        { value: 'scheduled', label: this.t('scheduled_recommended'), name: this.t('scheduled_recommended') },
        { value: 'realtime', label: this.t('real_time'), name: this.t('real_time') },
        { value: 'manual', label: this.t('manual'), name: this.t('manual') }
      ]
    },
    intervalOptions() {
      return this.intervals.map(i => ({
        value: i.value,
        label: i.label,
        name: i.label
      }))
    },
    formattedLastSync() {
      if (!this.syncSettings.last_sync_at) {
        return this.t('never')
      }

      // Parse MySQL datetime format (YYYY-MM-DD HH:MM:SS)
      const dateStr = this.syncSettings.last_sync_at.replace(' ', 'T')
      const lastSync = new Date(dateStr)

      if (isNaN(lastSync.getTime())) {
        return this.syncSettings.last_sync_at
      }

      const now = new Date()
      const diffMs = now - lastSync
      const diffMins = Math.floor(diffMs / 60000)

      if (diffMins < 1) {
        return this.t('just_now')
      } else if (diffMins < 60) {
        if (diffMins === 1) return this.t('minute_ago')
        return this.t('minutes_ago', { minutes: diffMins })
      } else {
        return lastSync.toLocaleString()
      }
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    'syncSettings.sync_mode': {
      handler(newVal, oldVal) {
        if (this.isDataLoaded && newVal !== oldVal && this.syncSettings.spreadsheet_id && !this.isAutoSaving) {
          this.autoSaveSettings()
        }
      }
    },
    'syncSettings.sync_interval': {
      handler(newVal, oldVal) {
        if (this.isDataLoaded && newVal !== oldVal && this.syncSettings.spreadsheet_id && !this.isAutoSaving) {
          this.autoSaveSettings()
        }
      }
    }
  },
  methods: {
    loadData() {
      // Load all settings from localized script immediately
      const gsData = window.formychat_admin_vars?.additional?.google_sheets
      if (gsData) {
        this.isConnected = gsData.connected || false
        if (gsData.intervals) {
          this.intervals = gsData.intervals
        }
        if (gsData.free_limit) {
          this.freeLimit = gsData.free_limit
        }
        if (gsData.sync_settings) {
          this.syncSettings = { ...this.syncSettings, ...gsData.sync_settings }
        }
        if (gsData.sync_stats) {
          this.syncStats = { ...this.syncStats, ...gsData.sync_stats }
        }
      }

      // Set loading state before async fetch (show skeleton when sheet is configured)
      if (this.isConnected && this.syncSettings.spreadsheet_id) {
        this.isLoadingSheets = true
      }

      // Only fetch spreadsheets async
      if (this.isConnected) {
        this.loadSpreadsheets()
      }

      // Mark data as loaded after a tick to prevent watcher from triggering
      this.$nextTick(() => {
        this.isDataLoaded = true
      })
    },
    updateGlobalData(settings = null, stats = null) {
      if (!window.formychat_admin_vars?.additional?.google_sheets) return

      if (settings) {
        window.formychat_admin_vars.additional.google_sheets.sync_settings = {
          ...window.formychat_admin_vars.additional.google_sheets.sync_settings,
          ...settings
        }
      }
      if (stats) {
        window.formychat_admin_vars.additional.google_sheets.sync_stats = {
          ...window.formychat_admin_vars.additional.google_sheets.sync_stats,
          ...stats
        }
      }
    },
    clearSpreadsheet() {
      this.syncSettings.spreadsheet_id = ''
      this.syncSettings.spreadsheet_name = ''
      this.selectedSheetId = ''
      this.showCreateMode = false
    },
    async loadSpreadsheets() {
      this.isLoadingSheets = true
      try {
        const response = await API.get('integrations/google-sheets/spreadsheets')
        if (response.data?.spreadsheets) {
          this.spreadsheets = response.data.spreadsheets
        }
      } catch (err) {
        console.log('Failed to load spreadsheets')
      } finally {
        this.isLoadingSheets = false
      }
    },
    async confirmSelectSheet() {
      if (!this.selectedSheetId) return

      const selected = this.spreadsheets.find(s => s.id === this.selectedSheetId)
      if (!selected) return

      this.isSelectingSheet = true
      this.error = ''

      try {
        const response = await API.post('integrations/google-sheets/sync-settings', {
          spreadsheet_id: selected.id,
          spreadsheet_name: selected.name,
          sync_mode: this.syncSettings.sync_mode,
          sync_interval: this.syncSettings.sync_interval,
        })

        if (response.data?.settings) {
          this.syncSettings = { ...this.syncSettings, ...response.data.settings }
          this.updateGlobalData(response.data.settings)
        }
        if (response.data?.stats) {
          this.syncStats = response.data.stats
          this.updateGlobalData(null, response.data.stats)
        }
        this.selectedSheetId = ''
        const syncedCount = response.data?.synced ?? 0
        this.success = this.t('spreadsheet_initialized_success', { name: selected.name, count: syncedCount })
        setTimeout(() => { this.success = '' }, 5000)
      } catch (error) {
        this.error = error.response?.data?.message || this.t('failed_to_setup_spreadsheet')
      } finally {
        this.isSelectingSheet = false
      }
    },
    async createNewSheet() {
      if (!this.newSheetName) return

      this.isCreatingSheet = true
      this.error = ''

      try {
        const response = await API.post('integrations/google-sheets/spreadsheets', {
          title: this.newSheetName
        })

        if (response.data?.spreadsheet) {
          const newSheet = response.data.spreadsheet
          this.spreadsheets.unshift({ id: newSheet.id, name: newSheet.name })
          this.syncSettings.spreadsheet_id = newSheet.id
          this.syncSettings.spreadsheet_name = newSheet.name
          this.updateGlobalData({ spreadsheet_id: newSheet.id, spreadsheet_name: newSheet.name })
          if (response.data?.stats) {
            this.syncStats = response.data.stats
            this.updateGlobalData(null, response.data.stats)
          }
          this.newSheetName = ''
          this.showCreateMode = false
          const syncedCount = response.data?.synced ?? 0
          this.success = this.t('spreadsheet_created_success', { name: newSheet.name, count: syncedCount })
          setTimeout(() => { this.success = '' }, 5000)
        }
      } catch (error) {
        this.error = error.response?.data?.message || this.t('failed_to_create_spreadsheet')
      } finally {
        this.isCreatingSheet = false
      }
    },
    async saveSettings() {
      this.isSaving = true
      this.error = ''
      this.success = ''

      try {
        const response = await API.post('integrations/google-sheets/sync-settings', {
          spreadsheet_id: this.syncSettings.spreadsheet_id,
          spreadsheet_name: this.syncSettings.spreadsheet_name,
          sync_mode: this.syncSettings.sync_mode,
          sync_interval: this.syncSettings.sync_interval,
        })

        if (response.data?.settings) {
          this.syncSettings = { ...this.syncSettings, ...response.data.settings }
          this.updateGlobalData(response.data.settings)
        }
        this.success = this.t('settings_saved')
        setTimeout(() => { this.success = '' }, 3000)
      } catch (err) {
        this.error = err.response?.data?.message || this.t('failed_to_save_settings')
      } finally {
        this.isSaving = false
      }
    },
    async autoSaveSettings() {
      this.error = ''
      this.isAutoSaving = true

      try {
        const response = await API.post('integrations/google-sheets/sync-settings', {
          spreadsheet_id: this.syncSettings.spreadsheet_id,
          spreadsheet_name: this.syncSettings.spreadsheet_name,
          sync_mode: this.syncSettings.sync_mode,
          sync_interval: this.syncSettings.sync_interval,
          save_only: true,
        })
        if (response.data?.settings) {
          this.updateGlobalData(response.data.settings)
        }
      } catch (err) {
        this.error = err.response?.data?.message || this.t('failed_to_save_settings')
      } finally {
        this.isAutoSaving = false
      }
    },
    async triggerManualSync() {
      this.isSyncing = true
      this.syncButtonState = null
      this.error = ''
      this.success = ''

      try {
        const response = await API.post('integrations/google-sheets/sync')

        if (response.data?.stats) {
          this.syncStats = response.data.stats
          this.updateGlobalData(null, response.data.stats)
        }
        if (response.data?.settings) {
          this.syncSettings = { ...this.syncSettings, ...response.data.settings }
          this.updateGlobalData(response.data.settings)
        }

        // Show synced state
        this.syncButtonState = 'synced'
        setTimeout(() => { this.syncButtonState = null }, 3000)
      } catch (error) {
        this.error = error.response?.data?.message || this.t('sync_failed')
        this.syncButtonState = null
      } finally {
        this.isSyncing = false
      }
    },
    triggerFullResync() {
      this.showResyncConfirmation = true
    },
    async executeFullResync() {
      this.showResyncConfirmation = false
      this.isSyncing = true
      this.error = ''
      this.success = ''

      try {
        const response = await API.post('integrations/google-sheets/resync')

        if (response.data?.stats) {
          this.syncStats = response.data.stats
          this.updateGlobalData(null, response.data.stats)
        }

        if (response.data?.success) {
          this.success = this.t('full_resync_complete', { count: response.data.synced })
          setTimeout(() => { this.success = '' }, 3000)
        } else if (response.data?.error) {
          this.error = response.data.error
        }
      } catch (error) {
        this.error = error.response?.data?.message || this.t('resync_failed')
      } finally {
        this.isSyncing = false
      }
    }
  }
}
</script>
